/**
 * Company/project session state.
 *
 * One login can work on several companies at the same time, one per tab:
 *
 *  - `sessionStorage` (**per tab**): the company this tab works on
 *    (`current_company`, plus a snapshot of its data) and the selected project.
 *  - `localStorage` (**shared**): the session (`auth.currentUser`). Its
 *    `user.company` is the LAST company chosen in any tab, which is where a new
 *    tab starts.
 *
 * Every call to the API names the company of the tab (`x-company` header, see
 * services/baseRequest), so what another tab selects never changes where this
 * one reads and writes. The token still records the last selection
 * (/api/auth/select-company) for the clients that only carry the token.
 *
 * The project follows the same idea: the one a tab works on is in its
 * sessionStorage, and the last one chosen for each company is remembered in
 * localStorage (`last_projects`), so a new tab opens straight on it instead of
 * asking again.
 */

// Everything scoped to the selected project, cleared as a unit.
const PROJECT_SESSION_KEYS = [
  'current_project',
  'current_project_currency',
  'current_project_tz',
  'current_project_site_url',
  'current_date_filter',
  'recommendations_analytics_filter',
  'broadcasts_analytics_filter',
  'current_is_dfs',
];

// Company this tab works on: its id, and what the screens need to know about it.
const SESSION_COMPANY_KEY = 'current_company';
const SESSION_COMPANY_DATA_KEY = 'current_company_data';
const COMPANY_DATA_FIELDS = ['_id', 'name', 'logo', 'products', 'isDataForSales'];

// Last project chosen for each company, by company id. Shared by all tabs.
const LAST_PROJECTS_KEY = 'last_projects';
// What a tab needs to work on a project without waiting for the nav bar.
const REMEMBERED_PROJECT_KEYS = [
  'current_project',
  'current_project_currency',
  'current_project_tz',
  'current_project_site_url',
  'current_is_dfs',
];

// Set when the user switches company from inside the admin, read after the reload.
const SWITCH_NOTICE_KEY = 'company_switched_notice';

const toId = (company) => {
  if (!company) {
    return null;
  }
  return (company._id || company).toString();
};

const readStoredUser = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('auth.currentUser') || 'null');
    return stored && stored.user ? stored.user : null;
  } catch (e) {
    return null;
  }
};

// Every company of the session (the last one first), deduped by id.
const companiesOf = (user) => {
  const list = [];
  const seen = {};
  const add = (company) => {
    const id = toId(company);
    if (id && !seen[id]) {
      seen[id] = true;
      list.push(company);
    }
  };

  if (user) {
    add(user.company);
    if (Array.isArray(user.companies)) {
      user.companies.forEach(add);
    }
  }
  return list;
};

// Last company chosen in any tab, as stored with the session. Shared by all tabs.
export const getStoredCompanyId = () => {
  const user = readStoredUser();
  return toId(user ? user.company : null);
};

export const getStoredUserId = () => {
  const user = readStoredUser();
  return user && user._id ? user._id.toString() : null;
};

export const getSessionCompanyId = () => sessionStorage.getItem(SESSION_COMPANY_KEY);

// Company this tab works on; a tab that has not picked one yet is on the last one.
export const getActiveCompanyId = () => getSessionCompanyId() || getStoredCompanyId();

/**
 * Data of the company this tab works on (`_id`, `name`, `logo`, `products`...).
 * Use this instead of `auth.currentUser.user.company`, which is only the last
 * company chosen in any tab.
 */
export const getActiveCompany = () => {
  const id = getActiveCompanyId();
  if (!id) {
    return null;
  }

  try {
    const snapshot = JSON.parse(sessionStorage.getItem(SESSION_COMPANY_DATA_KEY) || 'null');
    if (snapshot && toId(snapshot) === id) {
      return snapshot;
    }
  } catch (e) {
    // fall through to the stored session
  }

  const known = companiesOf(readStoredUser()).find((company) => toId(company) === id);
  return known && typeof known === 'object' ? known : { _id: id };
};

export const stampSessionCompany = (company) => {
  const id = toId(company) || getStoredCompanyId();
  if (!id) {
    sessionStorage.removeItem(SESSION_COMPANY_KEY);
    sessionStorage.removeItem(SESSION_COMPANY_DATA_KEY);
    return;
  }

  sessionStorage.setItem(SESSION_COMPANY_KEY, id);

  const data = company && typeof company === 'object' && company.name
    ? company
    : companiesOf(readStoredUser()).find((item) => toId(item) === id && typeof item === 'object');

  if (data) {
    const snapshot = {};
    COMPANY_DATA_FIELDS.forEach((field) => {
      if (data[field] !== undefined) {
        snapshot[field] = data[field];
      }
    });
    snapshot._id = id;
    sessionStorage.setItem(SESSION_COMPANY_DATA_KEY, JSON.stringify(snapshot));
  } else {
    sessionStorage.removeItem(SESSION_COMPANY_DATA_KEY);
  }
};

export const clearProjectSession = () => {
  localStorage.removeItem('current_project');
  PROJECT_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
};

export const clearCompanySession = () => {
  clearProjectSession();
  sessionStorage.removeItem(SESSION_COMPANY_KEY);
  sessionStorage.removeItem(SESSION_COMPANY_DATA_KEY);
};

const readLastProjects = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(LAST_PROJECTS_KEY) || '{}');
    return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
  } catch (e) {
    return {};
  }
};

const writeLastProjects = (projects) => {
  try {
    localStorage.setItem(LAST_PROJECTS_KEY, JSON.stringify(projects));
  } catch (e) {
    // a full or blocked storage only costs the shortcut: the tab asks for the project
  }
};

// Screens need a project to ask for anything: without one they must not mount.
export const hasSelectedProject = () => !!sessionStorage.getItem('current_project');

/**
 * Remembers the project of this tab as the last one of its company.
 * @param {boolean} [options.keepOther] only refresh what is remembered: when the
 *   company already remembers a different project, leave it (a tab reloading an
 *   older project must not take over the last choice).
 */
export const rememberCurrentProject = ({ keepOther = false } = {}) => {
  const company = getActiveCompanyId();
  const project = sessionStorage.getItem('current_project');
  if (!company || !project) {
    return;
  }

  const projects = readLastProjects();
  if (keepOther && projects[company] && projects[company].current_project !== project) {
    return;
  }

  const snapshot = {};
  REMEMBERED_PROJECT_KEYS.forEach((key) => {
    const value = sessionStorage.getItem(key);
    if (value !== null) {
      snapshot[key] = value;
    }
  });
  projects[company] = snapshot;
  writeLastProjects(projects);
};

/**
 * Stops remembering the project of the tab's company (archived, deleted, no
 * longer listed). With `projectId`, only when that is the one remembered.
 */
export const forgetLastProject = (projectId = null) => {
  const company = getActiveCompanyId();
  const projects = readLastProjects();
  if (!company || !projects[company]) {
    return;
  }
  if (projectId && projects[company].current_project !== projectId) {
    return;
  }
  delete projects[company];
  writeLastProjects(projects);
};

// A tab without a project starts on the last one chosen for its company.
const restoreLastProject = () => {
  if (hasSelectedProject()) {
    return false;
  }
  const company = getSessionCompanyId();
  const snapshot = company ? readLastProjects()[company] : null;
  if (!snapshot || typeof snapshot.current_project !== 'string' || !snapshot.current_project) {
    return false;
  }

  REMEMBERED_PROJECT_KEYS.forEach((key) => {
    if (typeof snapshot[key] === 'string') {
      sessionStorage.setItem(key, snapshot[key]);
    }
  });
  return true;
};

// Set when syncSessionCompany drops a project, so the nav bar can tell the user
// why their selection is gone once it mounts.
let staleProjectDropped = false;

export const consumeStaleProjectNotice = () => {
  const dropped = staleProjectDropped;
  staleProjectDropped = false;
  return dropped;
};

// The switch reloads the app: the confirmation is shown by the nav bar afterwards.
export const markCompanySwitched = () => sessionStorage.setItem(SWITCH_NOTICE_KEY, '1');

export const consumeCompanySwitchedNotice = () => {
  const switched = !!sessionStorage.getItem(SWITCH_NOTICE_KEY);
  sessionStorage.removeItem(SWITCH_NOTICE_KEY);
  return switched;
};

/**
 * Makes sure this tab has a company of the current session, and a project when
 * one is remembered for that company:
 *  - a tab that already works on a company keeps it, whatever other tabs select;
 *  - a new tab starts on the last company chosen (localStorage);
 *  - a tab whose company is not part of the session any more (someone else
 *    signed in on this browser, or the access was removed) goes back to the last
 *    one and loses its project;
 *  - a tab without a project (new, or just switched company) takes the last one
 *    chosen for its company. The nav bar still checks it against the list.
 *
 * Runs from the router guard, before any screen can read `current_project`.
 * @returns {boolean} true when a stale project was dropped.
 */
export const syncSessionCompany = () => {
  const dropped = alignSessionCompany();
  restoreLastProject();
  return dropped;
};

const alignSessionCompany = () => {
  const user = readStoredUser();
  const last = toId(user ? user.company : null);
  const tab = getSessionCompanyId();

  if (!last) {
    return false;
  }
  if (tab && companiesOf(user).some((company) => toId(company) === tab)) {
    return false;
  }

  const dropped = !!tab && !!sessionStorage.getItem('current_project');
  if (tab) {
    clearProjectSession();
  }
  stampSessionCompany(user.company);

  staleProjectDropped = staleProjectDropped || dropped;
  return dropped;
};

// Several requests fail together: only the first one restarts the tab.
let recovering = false;

/**
 * The API refused the company of this tab (COMPANY_NOT_ALLOWED): the user no
 * longer belongs to it. Forget it and restart the tab on a company that is
 * still theirs, instead of leaving every screen failing.
 */
export const recoverCompanySession = () => {
  if (recovering || typeof window === 'undefined') {
    return;
  }
  recovering = true;

  const rejected = getActiveCompanyId();
  let remaining = 0;

  try {
    const stored = JSON.parse(localStorage.getItem('auth.currentUser') || 'null');
    if (stored && stored.user) {
      const companies = companiesOf(stored.user).filter((company) => toId(company) !== rejected);
      remaining = companies.length;

      if (remaining) {
        stored.user.companies = companies;
        if (toId(stored.user.company) === rejected) {
          stored.user.company = companies[0];
        }
        localStorage.setItem('auth.currentUser', JSON.stringify(stored));
      } else {
        localStorage.removeItem('auth.currentUser');
      }
    }
  } catch (e) {
    // an unreadable session ends at the login below
  }

  clearCompanySession();

  let target = '/login';
  if (remaining === 1) {
    target = '/';
  } else if (remaining > 1) {
    target = '/select-company';
  }
  window.location.assign(target);
};
